import { Component } from '@theme/component';

/**
 * Little Flock video row: short vertical clips that play muted only while on screen
 * (they never download before they come near the viewport), pause when scrolled away,
 * and offer a tap-for-sound toggle. Each clip sits inside a native button, so tap, click, Enter
 * or Space plays or pauses it, and a clip the shopper paused stays paused. With reduced motion, clips wait for a tap.
 *
 * @extends {Component}
 */
class LfVideoRow extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  #controller = new AbortController();

  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.6 });
    for (const video of this.videos) {
      this.#observer.observe(video);
      const toggle = video.closest('[data-lf-toggle]');
      toggle?.addEventListener('click', () => this.#togglePlay(video), { signal });
      video.addEventListener('play', () => toggle?.setAttribute('aria-pressed', 'true'), { signal });
      video.addEventListener('pause', () => toggle?.setAttribute('aria-pressed', 'false'), { signal });
    }

    for (const button of this.querySelectorAll('[data-lf-sound]')) {
      button.addEventListener('click', () => this.#toggleSound(/** @type {HTMLButtonElement} */ (button)), { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  /** @returns {HTMLVideoElement[]} */
  get videos() {
    return Array.from(this.querySelectorAll('video'));
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      const video = /** @type {HTMLVideoElement} */ (entry.target);
      if (entry.isIntersecting) {
        if (!this.#motionQuery.matches && video.dataset.lfUserPaused !== 'true') this.#play(video);
      } else {
        video.pause();
      }
    }
  };

  /** @param {HTMLVideoElement} video */
  #play(video) {
    video.muted = video.dataset.lfSoundOn !== 'true';
    video.play().catch(() => {});
  }

  /** @param {HTMLVideoElement} video */
  #togglePlay(video) {
    if (video.paused) {
      video.dataset.lfUserPaused = 'false';
      this.#play(video);
    } else {
      video.dataset.lfUserPaused = 'true';
      video.pause();
    }
  }

  /** @param {HTMLButtonElement} button */
  #toggleSound(button) {
    const video = button.closest('.lf-video')?.querySelector('video');
    if (!video) return;

    const turnOn = video.muted;
    for (const other of this.videos) {
      other.muted = true;
      other.dataset.lfSoundOn = 'false';
    }
    for (const other of this.querySelectorAll('[data-lf-sound]')) {
      other.setAttribute('aria-pressed', 'false');
    }

    if (turnOn) {
      video.dataset.lfSoundOn = 'true';
      video.muted = false;
      button.setAttribute('aria-pressed', 'true');
      if (video.paused) video.play().catch(() => {});
    }
  }
}

if (!customElements.get('lf-video-row')) {
  customElements.define('lf-video-row', LfVideoRow);
}
